import { useCallback } from 'react';
import type { SiteId } from '@interfaces/IScheduler';
import { usePullPlan } from './usePullPlan';
import { useSchedulerActions } from './useSchedulerStore';

/**
 * Take the server's word for the schedule after it refused — or never took —
 * a write this page had already applied. The plan is re-read and rebased
 * (`usePullPlan`: a plan field the server didn't take goes back to the saved
 * value, the user's unsaved Gantt edits stay), and the execution facet is
 * replaced outright: between loads the local execution map is authoritative,
 * so nothing else would ever undo an optimistic progress, tick, comment, area
 * or stock write.
 *
 * Resolves true once the page shows the server's state, false when the read
 * itself failed (nothing moved; the stale banner offers the reload).
 */
export function useRestoreServerState(siteId: SiteId): (scheduleNumber: string) => Promise<boolean> {
  const pullPlan = usePullPlan(siteId);
  const { executionRefreshed } = useSchedulerActions();

  return useCallback(
    async (scheduleNumber: string) => {
      const fresh = await pullPlan(scheduleNumber);
      if (!fresh) return false;
      executionRefreshed({ siteId, fresh: { ...fresh, siteId } });
      return true;
    },
    [pullPlan, executionRefreshed, siteId],
  );
}
